import { useEffect, useMemo, useState } from "react";
import {
  IconCheckbox,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconFileTypeCsv,
  IconFileSpreadsheet,
  IconShieldPlus,
  IconShieldX,
  IconSearch,
  IconShieldCheck,
  IconShoppingBag,
  IconShoppingBagCheck,
  IconSquare,
  IconUserCircle,
  IconX,
} from "@tabler/icons-react";
import CmsLayout from "@layouts/CmsLayout";
import useCmsUsers from "@hooks/cmsUsers/useCmsUsers";
import { listToursRequest } from "@services/tours/toursApi";
import { resolveMediaUrl } from "@utils/media/mediaUrl";
import "@assets/css/cms_users/panel.css";

const pageLimit = 20;
const roleFilterOptions = [
  { value: "all", label: "All" },
  { value: "super_admin", label: "Super admin" },
  { value: "admin", label: "Admin" },
  { value: "customer", label: "Customer" },
];

function formatDate(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getStatusLabel(user) {
  if (user?.isActive === false) return "Inactive";
  if (Number(user?.status) === 1) return "Active";
  return `Status ${user?.status ?? "N/A"}`;
}

function getDisplayEmail(user = {}) {
  const email = String(user?.email || "");
  const alternativeEmail = String(user?.emailAlternative || "").trim();
  if (
    email.toLowerCase().endsWith("@privaterelay.appleid.com") &&
    alternativeEmail
  ) {
    return alternativeEmail;
  }
  return email;
}

function getTranslatedName(name) {
  if (!name || typeof name !== "object") return "N/A";
  const resolveValue = (entry) => {
    if (typeof entry === "string") return entry;
    if (entry && typeof entry === "object") {
      return entry.value || entry.message || entry.text || "";
    }
    return "";
  };
  return (
    resolveValue(name.en) ||
    resolveValue(name.es) ||
    Object.values(name).map(resolveValue).find(Boolean) ||
    "N/A"
  );
}

function getTourId(tour) {
  return String(tour?.id || tour?.tourId || tour?.tur_uuid || "");
}

function getAssignedAdminTours(user = {}) {
  const assignedTours =
    user?.["turs-admin"] ||
    user?.tursAdmin ||
    user?.toursAdmin ||
    user?.adminTours ||
    [];
  return Array.isArray(assignedTours) ? assignedTours : [];
}

function sortRoles(roles = []) {
  const roleOrder = {
    super_admin: 1,
    admin: 2,
    customer: 3,
  };

  return [...roles].sort((firstRole, secondRole) => {
    const firstOrder = roleOrder[firstRole?.code] || 99;
    const secondOrder = roleOrder[secondRole?.code] || 99;
    if (firstOrder !== secondOrder) return firstOrder - secondOrder;
    return String(firstRole?.name || firstRole?.code || "").localeCompare(
      String(secondRole?.name || secondRole?.code || ""),
    );
  });
}

function normalizeToursBody(body) {
  if (Array.isArray(body?.tours)) return body.tours;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body)) return body;
  return [];
}

function normalizeToursPagination(body = {}) {
  const source = body?.pagination || {};
  return {
    page: Number(source?.page || 1),
    pageSize: Number(source?.pageSize || 10),
    total: Number(source?.total || 0),
    totalPages: Math.max(1, Number(source?.totalPages || 1)),
  };
}

function CmsUsersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState("all");
  const [roleFilterOpen, setRoleFilterOpen] = useState(false);
  const [tourSearch, setTourSearch] = useState("");
  const [tourPage, setTourPage] = useState(1);
  const [tours, setTours] = useState([]);
  const [toursPagination, setToursPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const [toursLoading, setToursLoading] = useState(false);
  const [toursError, setToursError] = useState("");
  const {
    users,
    pagination,
    selectedUser,
    loading,
    detailLoading,
    exporting,
    roleSubmitting,
    tourAccessSubmittingId,
    error,
    fetchUsers,
    fetchUserDetail,
    exportUser,
    assignAdminRole,
    removeAdminRole,
    assignAdminTour,
    removeAdminTour,
    clearSelectedUser,
  } = useCmsUsers();
  const [exportFormat, setExportFormat] = useState("csv");
  const [exportError, setExportError] = useState("");

  async function handleExportUser() {
    if (!selectedUser?.id || exporting) return;
    setExportError("");
    try {
      const response = await exportUser(selectedUser.id, exportFormat);
      const blob = response?.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The export file could not be downloaded.");
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const extension = exportFormat === "excel" ? "xls" : "csv";
      link.href = url;
      link.download = `user-${selectedUser.id}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message || "Could not export user information.");
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers({ page, limit: pageLimit, search, role: roleFilter });
    }, 350);
    return () => clearTimeout(timer);
  }, [fetchUsers, page, roleFilter, search]);

  useEffect(() => {
    setTourSearch("");
    setTourPage(1);
  }, [selectedUser?.id]);

  const canGoPrevious = pagination.page > 1;
  const canGoNext = pagination.page < pagination.totalPages;
  const selectedRoles = selectedUser?.roles || [];
  const selectedUserIsAdmin = selectedRoles.some(
    (role) => role.code === "admin",
  );
  const selectedUserIsSuperAdmin = selectedRoles.some(
    (role) => role.code === "super_admin",
  );
  const selectedAdminTourIds = useMemo(() => {
    const assignedTourIds = getAssignedAdminTours(selectedUser).map((tour) =>
      getTourId(tour),
    );
    const access = selectedUser?.adminAccess || {};
    const ids = Array.isArray(access?.tourIds)
      ? [...assignedTourIds, ...access.tourIds]
      : [...assignedTourIds];
    if (access?.tourId) ids.push(access.tourId);
    return new Set(ids.map((id) => String(id)));
  }, [selectedUser]);

  const rows = useMemo(() => users, [users]);
  const roleFilterLabel =
    roleFilterOptions.find((option) => option.value === roleFilter)?.label ||
    "All";

  useEffect(() => {
    if (!selectedUser || !selectedUserIsAdmin || selectedUserIsSuperAdmin) {
      setTours([]);
      setToursError("");
      return undefined;
    }

    let isMounted = true;
    const timer = setTimeout(async () => {
      setToursLoading(true);
      setToursError("");
      try {
        const response = await listToursRequest({
          page: tourPage,
          pageSize: 10,
          search: tourSearch,
        });
        if (!isMounted) return;
        setTours(normalizeToursBody(response.body));
        setToursPagination(normalizeToursPagination(response.body));
      } catch (err) {
        if (!isMounted) return;
        setTours([]);
        setToursError(err.message || "Could not load tours.");
      } finally {
        if (isMounted) setToursLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [
    selectedUser,
    selectedUserIsAdmin,
    selectedUserIsSuperAdmin,
    tourPage,
    tourSearch,
  ]);

  return (
    <CmsLayout activeModule="users" title="Users">
      <section className="cms-users-page">
        <article className="panel table-panel cms-users-panel">
          <div className="cms-users-head">
            <div>
              <p className="cms-users-kicker">System users</p>
              <h2>Users</h2>
            </div>
            <div className="cms-users-search">
              <IconSearch size={18} />
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Search by email, name, phone or Firebase ID"
              />
            </div>
          </div>

          {error ? (
            <p className="message-error cms-users-message">{error}</p>
          ) : null}
          {loading ? (
            <p className="cms-users-loading">Loading users...</p>
          ) : null}

          <div className="table-wrap cms-users-table-wrap">
            <table className="data-table cms-users-table">
              <thead>
                <tr>
                  <th>User</th>
                  {/* <th>Contact</th> */}
                  <th>
                    <div className="cms-users-role-filter">
                      <button
                        type="button"
                        className={`cms-users-role-filter-btn ${
                          roleFilter !== "all" ? "is-active" : ""
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setRoleFilterOpen((current) => !current);
                        }}
                      >
                        <span>Roles</span>
                        {roleFilter !== "all" ? (
                          <small>{roleFilterLabel}</small>
                        ) : null}
                        <IconChevronDown size={14} />
                      </button>
                      {roleFilterOpen ? (
                        <div className="cms-users-role-filter-menu">
                          {roleFilterOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={
                                roleFilter === option.value ? "is-selected" : ""
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                setRoleFilter(option.value);
                                setPage(1);
                                setRoleFilterOpen(false);
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </th>
                  <th>Purchase</th>
                  <th>Status</th>
                  <th>Last login</th>
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => fetchUserDetail(user.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div className="cms-user-cell">
                        <span className="cms-user-avatar">
                          <IconUserCircle size={22} />
                        </span>
                        <div>
                          <strong>
                            {user.name ||
                              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                              "N/A"}
                          </strong>
                          <small>{getDisplayEmail(user) || "No email"}</small>
                        </div>
                      </div>
                    </td>
                    {/*  <td>
                      <span>{user.phone || "N/A"}</span>
                      <small className="cms-users-muted">{user.countryCode || user.languageCode || ""}</small>
                    </td> */}
                    <td>
                      <div className="cms-users-role-list">
                        {sortRoles(user.roles || []).map((role) => (
                          <span
                            key={role.id || role.code}
                            className="cms-users-role"
                          >
                            {role.name || role.code}
                          </span>
                        ))}
                        {!user.roles?.length ? (
                          <span className="cms-users-muted">No roles</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className="cms-users-purchase-cell">
                        {user.hasPurchases ? (
                          <span
                            className={`cms-users-purchase-badge ${
                              user.hasPurchases ? "has-purchases" : ""
                            }`}
                            title={
                              user.hasPurchases
                                ? "User has purchases"
                                : "User has no purchases"
                            }
                          >
                            {user.hasPurchases ? (
                              <IconShoppingBagCheck size={16} />
                            ) : (
                              <></>
                              //<IconShoppingBag size={16} />
                            )}
                          </span>
                        ) : (
                          <></>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`cms-users-status ${user.isActive === false ? "is-inactive" : ""}`}
                      >
                        {getStatusLabel(user)}
                      </span>
                    </td>
                    <td>{formatDate(user.lastLoginAt)}</td>
                    {/*  <td>
                      <button
                        type="button"
                        className="app-icon-btn app-icon-btn--update"
                        onClick={() => fetchUserDetail(user.id)}
                        title="View user detail"
                        aria-label="View user detail"
                      >
                        <IconEye size={17} />
                      </button>
                    </td> */}
                  </tr>
                ))}
                {!rows.length && !loading ? (
                  <tr>
                    <td colSpan={6}>No users found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="cms-users-pagination">
            <button
              className="button-ghost"
              type="button"
              disabled={!canGoPrevious || loading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              <IconChevronLeft size={16} />
              Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.totalPages} ·{" "}
              {pagination.total} users
            </span>
            <button
              className="button-ghost"
              type="button"
              disabled={!canGoNext || loading}
              onClick={() =>
                setPage((prev) => Math.min(pagination.totalPages, prev + 1))
              }
            >
              Next
              <IconChevronRight size={16} />
            </button>
          </div>
        </article>

        {selectedUser || detailLoading ? (
          <div className="app-modal-overlay" role="presentation">
            <article
              className="app-modal app-modal-xl cms-users-detail-modal"
              role="dialog"
              aria-modal="true"
            >
              <div className="app-modal-header cms-users-modal-head">
                <div>
                  <p className="cms-users-kicker">User detail</p>
                  <h3>
                    {selectedUser?.name ||
                      getDisplayEmail(selectedUser) ||
                      "Loading..."}
                  </h3>
                </div>
                <button
                  className="app-icon-btn app-icon-btn--back"
                  type="button"
                  onClick={clearSelectedUser}
                  aria-label="Close user detail"
                >
                  <IconX size={18} />
                </button>
              </div>

              <div className="app-modal-body cms-users-detail">
                {detailLoading ? (
                  <p className="cms-users-loading">Loading detail...</p>
                ) : null}
                {selectedUser && !detailLoading ? (
                  <>
                    <div className="cms-users-detail-grid">
                      <div>
                        <span>Email</span>
                        <strong>
                          {getDisplayEmail(selectedUser) || "N/A"}
                        </strong>
                      </div>
                      <div>
                        <span>Alternative email</span>
                        <strong>
                          {selectedUser.emailAlternative || "N/A"}
                        </strong>
                      </div>
                      <div>
                        <span>Firebase ID</span>
                        <strong>{selectedUser.firebaseId || "N/A"}</strong>
                      </div>
                      <div>
                        <span>Created</span>
                        <strong>{formatDate(selectedUser.createdAt)}</strong>
                      </div>
                      {/* <div>
                        <span>Email verified</span>
                        <strong>
                          {selectedUser.emailVerified ? "Yes" : "No"}
                        </strong>
                      </div> */}
                    </div>

                    <section className="cms-users-export-card">
                      <div className="cms-users-export-copy">
                        <div className="cms-users-export-icon">
                          <IconFileSpreadsheet size={21} />
                        </div>
                        <div>
                          <h4>Export user information</h4>
                          <p>Download a complete copy of this user&apos;s data.</p>
                        </div>
                      </div>
                      <div className="cms-users-export-controls">
                        <div className="cms-users-format-toggle" role="group" aria-label="Export format">
                          <button
                            type="button"
                            className={exportFormat === "csv" ? "active" : ""}
                            onClick={() => setExportFormat("csv")}
                            disabled={exporting}
                          >
                            <IconFileTypeCsv size={17} />
                            CSV
                          </button>
                          <button
                            type="button"
                            className={exportFormat === "excel" ? "active" : ""}
                            onClick={() => setExportFormat("excel")}
                            disabled={exporting}
                          >
                            <IconFileSpreadsheet size={17} />
                            Excel
                          </button>
                        </div>
                        <button
                          type="button"
                          className="button-primary cms-users-export-btn"
                          onClick={handleExportUser}
                          disabled={exporting}
                        >
                          {exporting ? (
                            <span className="app-icon-btn-spinner" />
                          ) : (
                            <IconDownload size={16} />
                          )}
                          {exporting ? "Downloading..." : "Download"}
                        </button>
                      </div>
                    </section>
                    {exportError ? (
                      <p className="message-error cms-users-message">{exportError}</p>
                    ) : null}

                    <div className="cms-users-detail-section">
                      <h4>
                        <IconShieldCheck size={18} />
                        Roles
                      </h4>
                      <div className="cms-users-role-list">
                        {sortRoles(selectedUser.roles || []).map((role) => (
                          <span
                            key={role.id || role.code}
                            className="cms-users-role"
                          >
                            {role.name || role.code}
                          </span>
                        ))}
                        {!selectedUser.roles?.length ? (
                          <span className="cms-users-muted">
                            No roles assigned.
                          </span>
                        ) : null}
                      </div>
                      <div className="cms-users-admin-role-control">
                        {selectedUserIsSuperAdmin ? (
                          <p className="cms-users-role-note">
                            Super admin users cannot be modified from this
                            section.
                          </p>
                        ) : (
                          <button
                            type="button"
                            className={`cms-users-admin-role-btn ${selectedUserIsAdmin ? "is-remove" : "is-assign"}`}
                            disabled={roleSubmitting}
                            onClick={() => {
                              if (selectedUserIsAdmin) {
                                removeAdminRole(selectedUser.id);
                                return;
                              }
                              assignAdminRole(selectedUser.id);
                            }}
                          >
                            {roleSubmitting ? (
                              <span className="app-icon-btn-spinner" />
                            ) : selectedUserIsAdmin ? (
                              <IconShieldX size={18} />
                            ) : (
                              <IconShieldPlus size={18} />
                            )}
                            {selectedUserIsAdmin
                              ? "Remove admin role"
                              : "Assign admin role"}
                          </button>
                        )}
                      </div>
                    </div>

                    {selectedUserIsAdmin && !selectedUserIsSuperAdmin ? (
                      <div className="cms-users-detail-section">
                        <h4>Assigned tours</h4>
                        <div className="cms-users-tour-access-panel">
                          <label className="field-label app-form-field cms-users-tour-search">
                            <input
                              className="field-input"
                              value={tourSearch}
                              placeholder="Search tours"
                              onChange={(event) => {
                                setTourSearch(event.target.value);
                                setTourPage(1);
                              }}
                            />
                          </label>
                          {toursError ? (
                            <p className="message-error cms-users-message">
                              {toursError}
                            </p>
                          ) : null}
                          {toursLoading ? (
                            <p className="cms-users-loading">
                              Loading tours...
                            </p>
                          ) : null}
                          <div className="cms-users-tour-access-list">
                            {!toursLoading && !tours.length ? (
                              <p className="cms-users-muted">No tours found.</p>
                            ) : null}
                            {tours.map((tour) => {
                              const tourId = getTourId(tour);
                              const isAssigned =
                                selectedAdminTourIds.has(tourId);
                              const isSubmitting =
                                tourAccessSubmittingId === tourId;
                              return (
                                <article
                                  key={tourId || tour.code}
                                  className={`cms-users-tour-access-item ${isAssigned ? "is-selected" : ""}`}
                                >
                                  <div className="cms-users-tour-access-info">
                                    <img
                                      src={resolveMediaUrl(
                                        tour.banner?.url ||
                                          tour.bannerUrl ||
                                          tour.snapshot?.url ||
                                          "",
                                      )}
                                      alt=""
                                      onError={(event) => {
                                        event.currentTarget.style.display =
                                          "none";
                                      }}
                                    />
                                    <div>
                                      <strong>
                                        {getTranslatedName(
                                          tour.name || tour.description?.name,
                                        )}
                                      </strong>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className={`cms-users-tour-checkbox ${isAssigned ? "is-selected" : ""}`}
                                    disabled={
                                      !tourId || Boolean(tourAccessSubmittingId)
                                    }
                                    title={
                                      isAssigned
                                        ? "Remove tour from admin"
                                        : "Assign tour to admin"
                                    }
                                    aria-label={
                                      isAssigned
                                        ? "Remove tour from admin"
                                        : "Assign tour to admin"
                                    }
                                    onClick={() => {
                                      if (isAssigned) {
                                        removeAdminTour(
                                          selectedUser.id,
                                          tourId,
                                        );
                                        return;
                                      }
                                      assignAdminTour(selectedUser.id, tourId);
                                    }}
                                  >
                                    {isSubmitting ? (
                                      <span className="app-icon-btn-spinner" />
                                    ) : isAssigned ? (
                                      <IconCheckbox size={24} />
                                    ) : (
                                      <IconSquare size={24} />
                                    )}
                                  </button>
                                </article>
                              );
                            })}
                          </div>
                          <div className="cms-users-tour-pagination">
                            <button
                              className="button-ghost"
                              type="button"
                              disabled={tourPage <= 1 || toursLoading}
                              onClick={() =>
                                setTourPage((prev) => Math.max(1, prev - 1))
                              }
                            >
                              previous
                            </button>
                            <span>
                              page {toursPagination.page} of{" "}
                              {toursPagination.totalPages}
                            </span>
                            <button
                              className="button-ghost"
                              type="button"
                              disabled={
                                tourPage >= toursPagination.totalPages ||
                                toursLoading
                              }
                              onClick={() =>
                                setTourPage((prev) =>
                                  Math.min(
                                    toursPagination.totalPages,
                                    prev + 1,
                                  ),
                                )
                              }
                            >
                              next
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="cms-users-detail-section">
                      <h4>Purchases</h4>
                      <div className="cms-users-purchase-list">
                        {(selectedUser.purchases || []).map((purchase) => (
                          <article
                            key={purchase.id}
                            className="cms-users-purchase-card"
                          >
                            <div className="cms-users-purchase-head">
                              <strong>
                                {purchase.referenceCode || purchase.id}
                              </strong>
                              <span>{purchase.store || "store"}</span>
                            </div>
                            <p>
                              {purchase.currencyCode || ""}{" "}
                              {purchase.totalAmount ?? "0"} · Paid{" "}
                              {purchase.paidAt || "N/A"}
                            </p>
                            {(purchase.items || []).map((item) => (
                              <div
                                key={item.id}
                                className="cms-users-purchase-item"
                              >
                                <img
                                  src={
                                    item.tour?.imageUrl ||
                                    item.startPoint?.imageUrl ||
                                    ""
                                  }
                                  alt=""
                                  onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                  }}
                                />
                                <div>
                                  <strong>
                                    {getTranslatedName(item.tour?.name)}
                                  </strong>
                                  <small>
                                    {getTranslatedName(item.startPoint?.name)}
                                  </small>
                                </div>
                              </div>
                            ))}
                          </article>
                        ))}
                        {!selectedUser.purchases?.length ? (
                          <p className="cms-users-muted">No purchases found.</p>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </article>
          </div>
        ) : null}
      </section>
    </CmsLayout>
  );
}

export default CmsUsersPage;
